import { Grid, IconButton, Tooltip, Typography } from "@material-ui/core";
import LastPageIcon from "@material-ui/icons/LastPage";
import MenuOpenIcon from "@material-ui/icons/MenuOpen";
import React from "react";
import { useTranslation } from "react-i18next";
import {
  ECABIN,
  formatTimeWithTZOffset,
  shippingMethods,
} from "../Utils/Common";
import Title from "./Title";

const defaultIgnoredFields = [
  "id",
  "UserId",
  "metadata",
  "createdAt",
  "updatedAt",
];

const humanize = (value) =>
  (value || "")
    .toString()
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/^./, (letter) => letter.toUpperCase());

const getTranslation = (t, keys, fallback) => {
  for (let index = 0; index < keys.length; index++) {
    const key = keys[index];
    const translated = t(key);
    if (translated !== key) {
      return translated;
    }
  }
  return fallback;
};

const hasDisplayValue = (object, key) =>
  Object.prototype.hasOwnProperty.call(object || {}, key) &&
  object[key] !== null &&
  object[key] !== undefined &&
  object[key] !== "";

const formatIdentifier = (item, keys) => {
  if (item === null || item === undefined || item === "") {
    return "";
  }
  if (typeof item !== "object") {
    return item;
  }
  const key = keys.find((candidate) => hasDisplayValue(item, candidate));
  if (key) {
    return item[key];
  }
  return JSON.stringify(item);
};

const formatDeviceIdentifier = (item) =>
  formatIdentifier(item, ["serialNumber", "number", "id"]);

const formatSubAssemblyIdentifier = (item) =>
  formatIdentifier(item, ["number", "serialNumber", "id"]);

const formatIdentifierList = (value, formatter, separator = ", ") => {
  const values = Array.isArray(value) ? value : [value];
  return values
    .map((item) => formatter(item))
    .filter(Boolean)
    .join(separator);
};

const isDeviceAllocationField = (field) =>
  field === "devices" || field === "Devices";

const isSubAssemblyAllocationField = (field) =>
  field === "serialisedSubAssemblies" || field === "SerialisedSubAssemblies";

const appendAllocationField = (fields, label, value, formatter) => {
  if (!hasDisplayValue({ value }, "value")) {
    return;
  }
  const formattedValue = formatIdentifierList(value, formatter);
  if (formattedValue) {
    fields.push(`${label}: ${formattedValue}`);
  }
};

const formatGenericDevice = (t, item) => {
  const count = item.count ? item.count + " x " : "";
  const fields = [count + (item.name || item.sku || item.id)];

  if (hasDisplayValue(item, "price")) {
    fields.push(`${t("price")}: ${item.price}`);
  }
  if (hasDisplayValue(item, "discount")) {
    fields.push(`${t("orders.discount")}: ${item.discount}%`);
  }
  appendAllocationField(
    fields,
    t("orders.allocatedSerialNumbers"),
    item.serialNumbers,
    formatDeviceIdentifier,
  );
  appendAllocationField(
    fields,
    t("orders.allocatedSerialNumbers"),
    item.Devices || item.devices,
    formatDeviceIdentifier,
  );
  appendAllocationField(
    fields,
    t("shipments.serialisedSubAssemblies"),
    item.SerialisedSubAssemblies || item.serialisedSubAssemblies,
    formatSubAssemblyIdentifier,
  );

  return fields.join(" - ");
};

const formatKnownValue = (t, field, value) => {
  if (field === "paymentStatus") {
    return t("paymentStates." + value);
  }
  if (field === "status") {
    return t("shipmentStates." + value);
  }
  if (field === "shippingMethod") {
    const method = shippingMethods.find((item) => item.value === value);
    return method ? t("shippingMethods." + method.value) : value;
  }
  if (field === "shipmentDirection") {
    return t("shipments." + value);
  }
  if (field === "genericDevices") {
    return value
      .map((item) => formatGenericDevice(t, item))
      .join("\n");
  }
  if (isDeviceAllocationField(field)) {
    return (
      formatIdentifierList(value, formatDeviceIdentifier, "\n") ||
      (Array.isArray(value) && !value.length ? t("dataRemoved") : t("yes"))
    );
  }
  if (isSubAssemblyAllocationField(field)) {
    return (
      formatIdentifierList(value, formatSubAssemblyIdentifier, "\n") ||
      (Array.isArray(value) && !value.length ? t("dataRemoved") : t("yes"))
    );
  }
  return undefined;
};

const formatActionValue = (t, field, value) => {
  if (value === null || value === undefined || value === "") {
    return t("dataRemoved");
  }
  if (typeof value === "boolean") {
    return t(value ? "yes" : "no");
  }
  if (Array.isArray(value)) {
    return formatKnownValue(t, field, value) || JSON.stringify(value, null, 2);
  }
  if (typeof value === "object") {
    return Object.keys(value)
      .map((key) => `${humanize(key)}: ${formatActionValue(t, key, value[key])}`)
      .join("\n");
  }
  return formatKnownValue(t, field, value) || value;
};

const getFieldLabel = (t, field, labelPrefixes) =>
  isDeviceAllocationField(field)
    ? t("orders.allocatedSerialNumbers")
    : isSubAssemblyAllocationField(field)
      ? t("shipments.serialisedSubAssemblies")
      : getTranslation(
          t,
          [
            ...labelPrefixes.map((prefix) => prefix + "." + field),
            "ticketList." + field,
            field,
          ],
          humanize(field),
        );

export function ChangesHistory({
  actions,
  actionKey,
  labelPrefixes = [],
  ignoredFields = [],
}) {
  const { t } = useTranslation();
  const sort = (a, b) => b.id - a.id;
  const hiddenFields = [...defaultIgnoredFields, ...ignoredFields];
  const historyActions = Array.isArray(actions) ? actions : [];

  return (
    <Grid
      item
      container
      spacing={1}
      xs={12}
      style={{ height: window.innerHeight - 150, overflow: "scroll" }}
    >
      <Grid item xs={12}>
        <Title>{t("ticketList.changesHistory")}</Title>
      </Grid>
      {[...historyActions].sort(sort).map((action, index) => (
        <Grid
          item
          container
          xs={12}
          key={(actionKey || "action") + "-" + (action.id || index)}
          style={{
            padding: 10,
            backgroundColor: index % 2 === 0 ? "rgba(245, 245, 245, 1)" : "",
          }}
        >
          <Grid item style={{ marginRight: 20 }} xs={2}>
            <Typography>{action.User?.fullName}</Typography>
            <Typography style={{ fontSize: 12 }}>
              {formatTimeWithTZOffset(action.updatedAt, ECABIN)}
            </Typography>
          </Grid>
          <Grid item xs={9}>
            <Typography>{t("ticketList.changes")}</Typography>
            <hr />
            {Object.keys(action.changes || {}).map((field) => {
              if (hiddenFields.find((hiddenField) => hiddenField === field)) {
                return null;
              }
              return (
                <Grid item key={field}>
                  <Typography style={{ fontWeight: "bold" }}>
                    {getFieldLabel(t, field, labelPrefixes)}
                  </Typography>
                  <Typography style={{ whiteSpace: "pre-wrap" }}>
                    {formatActionValue(t, field, action.changes[field])}
                  </Typography>
                </Grid>
              );
            })}
          </Grid>
        </Grid>
      ))}
    </Grid>
  );
}

export function ChangesHistoryLayout({
  actions,
  actionKey,
  children,
  labelPrefixes,
  showHistory,
  setShowHistory,
  spacing = 1,
  style,
  width,
}) {
  const { t } = useTranslation();
  const showSidePanel = width !== "xs" && showHistory;

  return (
    <Grid container spacing={spacing} style={style}>
      <Grid item xs={showSidePanel ? 8 : 12}>
        <Grid
          container
          item
          xs={12}
          spacing={spacing}
          alignContent="flex-start"
          style={{
            borderRight: showSidePanel ? "1px solid #000" : "",
            height: window.innerHeight - 150,
            overflow: "scroll",
            position: "relative",
          }}
        >
          {width !== "xs" ? (
            <Grid
              item
              xs={12}
              style={{
                display: "flex",
                justifyContent: "flex-end",
                minHeight: 42,
              }}
            >
              <Tooltip
                title={showHistory ? t("hideSidePanel") : t("showSidePanel")}
              >
                <IconButton
                  onClick={() => {
                    setShowHistory(!showHistory);
                  }}
                >
                  {showHistory ? <LastPageIcon /> : <MenuOpenIcon />}
                </IconButton>
              </Tooltip>
            </Grid>
          ) : null}
          {children}
        </Grid>
      </Grid>
      {showSidePanel ? (
        <Grid item xs={4}>
          <ChangesHistory
            actions={actions}
            actionKey={actionKey}
            labelPrefixes={labelPrefixes}
          />
        </Grid>
      ) : null}
    </Grid>
  );
}

export default ChangesHistory;
